
/**
 * Advanced text matching utility for finding snippets in large documents
 * with support for fuzzy matching, quote normalization, and whitespace flexibility.
 */

export interface MatchResult {
  start: number;
  end: number;
  method: 'exact' | 'fuzzy' | 'anchor';
}

/**
 * Normalizes text by removing all whitespace and quotes for ultra-fuzzy matching
 */
const normalizeUltraFuzzy = (text: string): string => {
  return text
    .replace(/\s+/g, '')
    .replace(/["'״״]/g, '')
    .toLowerCase();
};

/**
 * Finds the occurrence of target in text that is best matched given a hint index.
 */
const findBestOccurrence = (text: string, target: string, hint: number): number => {
  const forwardIdx = text.indexOf(target, hint);
  const backwardIdx = text.lastIndexOf(target, hint);

  if (forwardIdx === -1) return backwardIdx;
  if (backwardIdx === -1) return forwardIdx;

  const forwardDiff = Math.abs(forwardIdx - hint);
  const backwardDiff = Math.abs(hint - backwardIdx);

  // Favor forward matches for chunk-based hints unless backward is significantly closer
  return forwardDiff <= (backwardDiff + 20) ? forwardIdx : backwardIdx;
};

/**
 * Maps a normalized range back to the actual string range
 */
const mapNormalizedRangeToActual = (text: string, normStart: number, normLength: number): { start: number, end: number } => {
  let actualPos = 0;
  let normalizedCount = 0;
  const lowerText = text.toLowerCase();

  // Find start
  while (actualPos < text.length && normalizedCount < normStart) {
    const char = lowerText[actualPos];
    if (!/\s/.test(char) && !/["'״״]/.test(char)) {
      normalizedCount++;
    }
    actualPos++;
  }

  // Skip leading whitespace/quotes in actual text
  while (actualPos < text.length && (/\s/.test(lowerText[actualPos]) || /["'״״]/.test(lowerText[actualPos]))) {
    actualPos++;
  }
  const actualStart = actualPos;

  // Find end
  let matchedRealChars = 0;
  while (actualPos < text.length && matchedRealChars < normLength) {
    const char = lowerText[actualPos];
    if (!/\s/.test(char) && !/["'״״]/.test(char)) {
      matchedRealChars++;
    }
    actualPos++;
  }

  // Include trailing quotes if they exist
  while (actualPos < text.length && /["'״״]/.test(lowerText[actualPos])) {
    actualPos++;
  }

  return { start: actualStart, end: actualPos };
};

/**
 * Core matching function that attempts multiple strategies to find a snippet in text
 */
export function findSnippetInText(
  text: string,
  snippet: string,
  hintOffset: number = 0,
): MatchResult | null {
  if (!text || !snippet) return null;

  // Strategy 1: Exact match near hint
  const bestIdx = findBestOccurrence(text, snippet, hintOffset);
  if (bestIdx !== -1 && Math.abs(bestIdx - hintOffset) < 5000) {
    return { start: bestIdx, end: bestIdx + snippet.length, method: 'exact' };
  }

  // Strategy 2: Ultra-fuzzy matching (normalized content)
  const normSnippet = normalizeUltraFuzzy(snippet);
  if (!normSnippet) {
    return null;
  }

  const normText = normalizeUltraFuzzy(text);
  // Calculate normalized hint position
  const normHint = normalizeUltraFuzzy(text.substring(0, hintOffset)).length;

  let bestNormIdx = -1;
  let minNormDiff = Infinity;
  let currNorm = normText.indexOf(normSnippet);

  while (currNorm !== -1) {
    const diff = Math.abs(currNorm - normHint);
    if (diff < minNormDiff) {
      minNormDiff = diff;
      bestNormIdx = currNorm;
    }
    currNorm = normText.indexOf(normSnippet, currNorm + 1);
  }

  if (bestNormIdx !== -1 && Math.abs(bestNormIdx - normHint) < 10000) {
    const actualRange = mapNormalizedRangeToActual(text, bestNormIdx, normSnippet.length);
    return { ...actualRange, method: 'fuzzy' };
  }

  // Strategy 3: Anchor-based matching (for large snippets that might have interior drift)
  if (snippet.length > 60) {
    const startAnchor = normalizeUltraFuzzy(snippet.substring(0, 30));
    const endAnchor = normalizeUltraFuzzy(snippet.substring(snippet.length - 30));

    let bestStartNormIdx = -1;
    let minStartDiff = Infinity;
    let currStartNorm = normText.indexOf(startAnchor);
    while (currStartNorm !== -1) {
      const diff = Math.abs(currStartNorm - normHint);
      if (diff < minStartDiff) {
        minStartDiff = diff;
        bestStartNormIdx = currStartNorm;
      }
      currStartNorm = normText.indexOf(startAnchor, currStartNorm + 1);
    }

    if (bestStartNormIdx !== -1 && Math.abs(bestStartNormIdx - normHint) < 10000) {
      const normEndIdx = normText.indexOf(endAnchor, bestStartNormIdx);
      // Ensure the distance between anchors is reasonable (max 4x snippet length)
      if (normEndIdx !== -1 && (normEndIdx - bestStartNormIdx) < snippet.length * 4) {
        const actualStartRange = mapNormalizedRangeToActual(text, bestStartNormIdx, startAnchor.length);
        const actualEndRange = mapNormalizedRangeToActual(text, normEndIdx, endAnchor.length);
        return { start: actualStartRange.start, end: actualEndRange.end, method: 'anchor' };
      }
    }
  }

  return null;
}
